import React, { createContext, useReducer, useContext, ReactNode } from "react";

export type Message = {
  id: number;
  text: string;
  severity: "success" | "error" | "info" | "warning";
};

type State = {
  messages: Message[];
};

type Action =
  | { type: "PUSH"; payload: Omit<Message, "id"> }
  | { type: "REMOVE"; payload: { id: number } };

const initialState: State = { messages: [] };

// unique self-contained ID (avoids global nextId)
const newId = () => Date.now() + Math.random();

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "PUSH":
      return {
        messages: [...state.messages, { id: newId(), ...action.payload }],
      };
    case "REMOVE":
      return {
        messages: state.messages.filter(m => m.id !== action.payload.id),
      };
    default:
      return state;
  }
}

const SystemMessageContext = createContext<{
  messages: Message[];
  showMessage: (text: string, severity: Message["severity"]) => void;
  hideMessage: (id: number) => void;
}>({
  messages: [],
  showMessage: () => {},
  hideMessage: () => {},
});

export const useSystemMessage = () => useContext(SystemMessageContext);

export const SystemMessageProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const showMessage = (text: string, severity: Message["severity"]) => {
    dispatch({ type: "PUSH", payload: { text, severity } });
  };

  const hideMessage = (id: number) => {
    dispatch({ type: "REMOVE", payload: { id } });
  };

  return (
    <SystemMessageContext.Provider
      value={{
        messages: state.messages,
        showMessage,
        hideMessage,
      }}
    >
      {children}
    </SystemMessageContext.Provider>
  );
};
